import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { LanguageService } from '../i18n/language.service';
import { Tree } from './tree';
import { I18N } from './tree.component.i18n';

/**
 * Diese Komponente ist eine allgemeine Komponente zur Darstellung von Daten in einer Baum-Struktur.
 * Input:
 *  title:          die Überschrift der Baum-Ansicht
 *  columnTitle:    die Überschrift der Tabellenspalte welche die nodee enthält
 *  tree:           die Daten welche dargestellt werden sollen, wird nicht veändert
 *  icon:           das Icon welches den nodeen vorangestellt wird.
 *  nodeIdToExpand: das node welches aufgeklappt werden soll.
 *
 * Output:
 *  nodeSelected: wirft ein Event ein node ausgewählt wurde. Es enthält seine ID.
 *  newnode: wirft ein Event wenn ein neues node erstellt werden soll. Es enthält die ID des Vater Knotens.
 *
 * @author femu
 */
@Component({
    selector: 'common-tree',
    templateUrl: './tree.component.html'
})
export class TreeComponent implements OnChanges, OnDestroy {
    @Input() title: string;
    @Input() columnTitle: string;
    @Input() tree: Tree[];
    @Input() icon: string;
    @Input() nodeIdToExpand: string;
    @Input() hasEditIcon: boolean;
    @Input() enableSearch: boolean;
    @Output() nodeSelected = new EventEmitter<Tree>();
    @Output() newNode = new EventEmitter<Tree>();
    @Output() editNode = new EventEmitter<Tree>();

    /** Datenstruktur für die Darstellung des Baums */
    public viewTree: Tree[];
    public form: UntypedFormGroup;
    public searchName: string;

    i18n: any;
    languageSubscription: Subscription;

    private formChangeSubscription: Subscription;

    constructor(fb: UntypedFormBuilder, private languageService: LanguageService) {
        this.form = fb.group({
            searchName: ['']
        });
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnChanges() {
        if (this.tree) {
            this.resetViewTree();
            // Öffne einen Knoten falls nötig
            this.expandTreeUntilNodes([this.nodeIdToExpand]);
            if (this.formChangeSubscription) {
                this.formChangeSubscription.unsubscribe();
            }
            // Abonniere die Änderungen im Suchfeld und führe Suche aus.
            this.formChangeSubscription = this.form.get('searchName').valueChanges
                .pipe(
                    filter((s: string) => s.length >= 3),
                    debounceTime(300),
                    map(s => this.search(s))
                )
                .subscribe();
        }
    }

    ngOnDestroy() {
        if (this.formChangeSubscription) {
            this.formChangeSubscription.unsubscribe();
        }
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }

    onNodeSelected(node: Tree) {
        if (node.hasAccess) {
            this.nodeSelected.emit(node);
        }
    }

    onNewNode(node: Tree) {
        this.newNode.emit(node);
    }

    onEditNode(node: Tree) {
        this.editNode.emit(node);
    }

    /**
     * Öffnet den Baum an einem bestimmten Knoten. Die Datenstruktur für die Darstellung des Baums (viewTree) wird aufgebaut.
     */
    toggle(node: Tree) {
        if (!node.expanded) {
            this.openNodeOnViewTree(node);
        } else {
            this.closeNodeOnViewTree(node);
        }
    }

    private search(searchName: string) {
        this.resetViewTree();
        const nodeIds: string[] = [];
        this.findNodeIdsInTree(searchName, this.tree, nodeIds);
        this.expandTreeUntilNodes(nodeIds);
    }

    private resetViewTree() {
        // kopiere Daten in neues Array
        this.viewTree = this.tree.slice();
        this.closeNodes(this.viewTree);
    }

    private closeNodes(tree: Tree[]) {
        tree.forEach(element => {
            element.expanded = false;
            this.closeNodes(element.children);
        });
    }

    /**
     * Durchläuft den Baum und sucht nach Knoten mit den entsprechenden Namen, es können mehrere gefunden werden.
     */
    private findNodeIdsInTree(name: string, data: Tree[], foundIds: string[]) {
        data.forEach(node => {
            if (node.name.toLowerCase().indexOf(name.toLowerCase()) >= 0) {
                foundIds.push(node.id);
            }
            this.findNodeIdsInTree(name, node.children, foundIds);
        });
    }

    /**
     * Fügt die Kinder des Knotens der Baumdarstellung hinzu.
     */
    private openNodeOnViewTree(node: Tree) {
        let index = this.viewTree.indexOf(node);
        index++;
        node.expanded = true;
        node.children.forEach((child: Tree) => {
            if (node.padding) {
                child.padding = 15 + node.padding;
            } else {
                child.padding = 15;
            }
            child.style = {
                // CSS property names
                'margin-left': child.padding + 'px'
            };
            child.hasChildren = true;
            this.viewTree.splice(index, 0, child);
            index++;
        });
    }

    /**
     * Entfernt alle Kinder und deren geöffneten Kinder des Knotens aus der Baumdarstellung.
     */
    private closeNodeOnViewTree(node: Tree) {
        node.expanded = false;
        node.children.forEach((child: Tree) => {
            const i = this.viewTree.indexOf(child);
            if (child.expanded && child.children) {
                child.expanded = false;
                node.children.forEach((ts: Tree) => {
                    this.closeNodeOnViewTree(ts);
                });
            }
            if (i >= 0) {
                this.viewTree.splice(i, 1);
            }
        });
    }

    /**
     * Klappt den Baum bis zu den übergebenen Knoten auf.
     */
    private expandTreeUntilNodes(nodeIds: string[]) {
        if (!nodeIds) {
            return;
        }
        const path: Tree[] = [];
        this.buildPathForExpand(nodeIds, this.tree, path);
        path.forEach(node => {
            this.toggle(node);
        });
    }

    /**
     * Such nach den übergenenen Knoten-Ids in der Datenstruktur und baut einen Knoten-Pfad auf.
     * Dieser Pfad dient dann zum Aufklappen des Baums.
     */
    private buildPathForExpand(nodeIds: string[], nodes: Tree[], path: Tree[]): boolean {
        let found: boolean = false;
        nodes.forEach(node => {
            // Füge erstmal jeden Knoten zum Pfad hinzu, und entferne Ihn erst wenn unter ihm kein Knoten gefunden wurde.
            path.push(node);
            if (nodeIds.indexOf(node.id) !== -1) {
                found = true;
                // Entferne gefundenen Knoten, da dieser nicht zum Pfad gehören soll.
                // path.splice(path.indexOf(node), 1);
            }
            // Durchsuche immer die Kinder vom Knoten.
            if (this.buildPathForExpand(nodeIds, node.children, path)) {
                node.expanded = false;
                found = true;
            } else {
                // Entferne Knoten in denen kein Knoten gefunden wurde.
                path.splice(path.indexOf(node), 1);
            }
        });
        return found;
    }

}
